import React, { useState, useMemo } from "react";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Trash2, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "blockquote", "link",
];

export default function BlogEditor({ initial, onSave, onCancel, onDelete }) {
  const isNew = !initial?.id;
  const [title, setTitle] = useState(initial?.title || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [excerpt, setExcerpt] = useState(initial?.excerpt || "");
  const [image, setImage] = useState(initial?.image || "");
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [publishedAt, setPublishedAt] = useState(
    initial?.published_at || new Date().toISOString().slice(0, 10),
  );
  const [published, setPublished] = useState(initial?.published !== false);
  const [content, setContent] = useState(initial?.content || "");
  const [saving, setSaving] = useState(false);

  const isValid = useMemo(() => title.trim() && content.trim() && excerpt.trim() && image.trim(), [title, content, excerpt, image]);

  const handleSave = async () => {
    if (!isValid) {
      toast.error("Título, extracto, imagen y contenido son obligatorios");
      return;
    }
    const payload = {
      title: title.trim(),
      slug: slug.trim() || undefined,
      excerpt: excerpt.trim(),
      content,
      image: image.trim(),
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      published_at: publishedAt,
      published,
    };
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        const r = await axios.post(`${API}/admin/blog`, payload, cfg);
        saved = r.data;
        toast.success("Artículo creado");
      } else {
        const r = await axios.patch(`${API}/admin/blog/${initial.id}`, payload, cfg);
        saved = r.data;
        toast.success("Artículo guardado");
      }
      onSave(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Borrar definitivamente "${initial.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${initial.id}`, cfg);
      toast.success("Artículo borrado");
      onDelete(initial.id);
    } catch {
      toast.error("Error al borrar");
    }
  };

  return (
    <div data-testid="blog-editor" className="bg-white border border-[color:var(--brand-border)] rounded-md p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow">{isNew ? "Nuevo artículo" : "Editando"}</p>
          <h3 className="font-serif text-2xl text-[color:var(--brand-primary)] mt-1">
            {isNew ? "Crear artículo del blog" : title || "(sin título)"}
          </h3>
        </div>
        <Button variant="ghost" onClick={onCancel} data-testid="blog-editor-cancel">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="eyebrow block mb-1.5">Título *</span>
          <Input data-testid="blog-editor-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Recetas otoñales…" />
        </label>
        <label className="block">
          <span className="eyebrow block mb-1.5">Slug (URL) — opcional, auto</span>
          <Input data-testid="blog-editor-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="se-genera-del-titulo" />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow block mb-1.5">Extracto (2 líneas) *</span>
          <Textarea data-testid="blog-editor-excerpt" rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Resumen corto que aparece en el listado del blog…" />
        </label>
        <label className="block md:col-span-2">
          <span className="eyebrow block mb-1.5">Imagen (URL) *</span>
          <Input data-testid="blog-editor-image" value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://…" />
          {image && (
            <img src={image} alt="preview" className="mt-2 h-32 rounded-sm object-cover border border-[color:var(--brand-border)]" onError={(e) => (e.target.style.display = "none")} />
          )}
        </label>
        <label className="block">
          <span className="eyebrow block mb-1.5">Tags (separados por coma)</span>
          <Input data-testid="blog-editor-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="recetas, otoño, membrillo" />
        </label>
        <label className="block">
          <span className="eyebrow block mb-1.5">Fecha de publicación</span>
          <Input data-testid="blog-editor-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 md:col-span-2 cursor-pointer select-none">
          <input
            data-testid="blog-editor-published"
            type="checkbox"
            checked={published}
            onChange={(e) => setPublished(e.target.checked)}
            className="w-4 h-4 accent-[color:var(--brand-sage)]"
          />
          <span className="text-sm text-[color:var(--brand-neutral)]">
            {published ? "Publicado (visible en la tienda)" : "Borrador (no visible)"}
          </span>
        </label>
      </div>

      <div>
        <span className="eyebrow block mb-1.5">Contenido *</span>
        <ReactQuill
          data-testid="blog-editor-content"
          theme="snow"
          value={content}
          onChange={setContent}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Escribe el contenido del artículo…"
        />
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-[color:var(--brand-border)]">
        {!isNew ? (
          <Button variant="ghost" onClick={handleDelete} data-testid="blog-editor-delete" className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" /> Borrar
          </Button>
        ) : <div />}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="blog-editor-cancel-2">Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !isValid} data-testid="blog-editor-save" className="bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] hover:opacity-90">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
